import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { JOURNEY_STAGES } from "@/lib/journey";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PartyPopper, Star, Heart, Loader2 } from "lucide-react";

function CelebrationEditor() {
  const { data } = useQuery({ queryKey: ["celebrations"], queryFn: async () => (await api.get("/agent/celebrations")).data });
  const [msgs, setMsgs] = useState(null);
  useEffect(() => { if (data && msgs === null) { setMsgs(data.messages); } }, [data, msgs]);
  const save = useMutation({
    mutationFn: async () => (await api.put("/agent/celebrations", { messages: msgs })).data,
    onSuccess: () => toast.success("Celebration messages saved"),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (!msgs) return <Card className="p-6 rounded-2xl"><Loader2 className="h-5 w-5 animate-spin text-primary" /></Card>;
  return (
    <Card className="p-4 sm:p-6 rounded-2xl min-w-0" data-testid="celebration-editor">
      <div className="flex items-center gap-2 mb-1"><PartyPopper className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-lg">Celebration moments</h3></div>
      <p className="text-xs text-muted-foreground mb-4">When you move a client to a stage, they see confetti and your message once. Leave a stage blank for no celebration.</p>
      <div className="space-y-3">
        {JOURNEY_STAGES.map((s) => (
          <div key={s.key}>
            <p className="text-xs font-semibold mb-1">{s.label}</p>
            <Textarea rows={2} value={msgs[s.key] || ""} onChange={(e) => setMsgs({ ...msgs, [s.key]: e.target.value })} placeholder="No celebration for this stage" className="rounded-xl" data-testid={`celebration-input-${s.key}`} />
          </div>
        ))}
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-full mt-4" data-testid="celebration-save-btn">{save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save messages"}</Button>
    </Card>
  );
}

function ReviewLinkEditor() {
  const { data } = useQuery({ queryKey: ["review-link"], queryFn: async () => (await api.get("/agent/review-link")).data });
  const [url, setUrl] = useState("");
  useEffect(() => { if (data) { setUrl(data.review_url); } }, [data]);
  const save = useMutation({
    mutationFn: async () => (await api.put("/agent/review-link", { review_url: url })).data,
    onSuccess: () => toast.success("Review link saved"),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Card className="p-4 sm:p-6 rounded-2xl min-w-0" data-testid="review-link-editor">
      <div className="flex items-center gap-2 mb-1"><Star className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-lg">Google review link</h3></div>
      <p className="text-xs text-muted-foreground mb-3">Clients see a "Leave a review" button when their journey reaches Keys. Paste your Google review link here.</p>
      <div className="flex flex-wrap gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://g.page/r/..." className="flex-1 min-w-[200px]" data-testid="review-link-input" />
        <Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-full" data-testid="review-link-save-btn">Save</Button>
      </div>
    </Card>
  );
}

function ReferralsList() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["referrals"], queryFn: async () => (await api.get("/agent/referrals")).data });
  const mark = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/agent/referrals/${id}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["referrals"] }),
  });
  return (
    <Card className="p-4 sm:p-6 rounded-2xl min-w-0" data-testid="referrals-list">
      <div className="flex items-center gap-2 mb-3"><Heart className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-lg">Referrals ({data.length})</h3></div>
      {data.length === 0 ? <p className="text-sm text-muted-foreground">No referrals yet. They'll appear here when a client sends one.</p> : (
        <div className="space-y-2">
          {data.map((r) => (
            <div key={r.id} className="p-3 rounded-xl bg-secondary text-sm" data-testid={`referral-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium">{r.name}</p>
                <button type="button" className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${r.status === "new" ? "bg-primary/15 text-primary hover:bg-primary/25" : "bg-status-complete/15 text-status-complete"}`} onClick={() => mark.mutate({ id: r.id, status: r.status === "new" ? "contacted" : "new" })} data-testid={`referral-status-${r.id}`}>{r.status === "new" ? "New · mark contacted" : "Contacted"}</button>
              </div>
              <p className="text-xs text-muted-foreground">{[r.phone, r.email].filter(Boolean).join(" · ")}</p>
              {r.note && <p className="text-xs mt-1">{r.note}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">From {r.referred_by || r.client_name} · {new Date(r.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function MilestoneSettings() {
  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-6">
      <CelebrationEditor />
      <div className="space-y-6 min-w-0">
        <ReviewLinkEditor />
        <ReferralsList />
      </div>
    </div>
  );
}
